import React, { useEffect, useState } from "react";
import { __ } from "@wordpress/i18n";
import { useResult } from "../../../../shared/formContext/hooks/useResult";

// Components
import ChartPie from "./../../../../components/ChartPie/ChartPie";
import Cta from "./../../../../components/Cta/Cta";
import Box from "./../../../../components/Box/Box";
import Tabs from "./../../../../components/Tabs/Tabs";
import ShareFullLink from "../../../../shared/components/ShareFullLink";
import IconContent from "@components/IconContent/IconContent";

// Tabs
import TabComposition from "./../Tabs/TabComposition";
import TabComparison from "./../Tabs/TabComparison";

// Helpers
import { utils } from "./../../../../helpers";

/** Constants for reusable values */
const COLORS = {
    TOP_BOX: "orange-100",
    GREEN: "green-100",
    YELLOW: "yellow-100",
    RED: "red-100",
    CHART_GREEN: "#0B9D4D",
    CHART_YELLOW: "#FFB400",
    CHART_RED: "#F7442C",
    CHART_REMAINING: "#F7F7F8",
};

const EFFORT_RATE_CRITICAL = 40;
const EFFORT_RATE_WARNING = 35;

/** Possible scenarios */
export type MacEsScenario = "prudente" | "ampliado";

/** Shape of a single scenario returned by the external solver */
export interface MacEsScenarioData {
    metadata: {
        TAN: number;
        periodInYears: number;
        euribor: number;
        banking: { spread: number };
        input: {
            rateType: string;
        };
    };
    results: {
        propertyValue: number;
        loanAmount: number;
        downPaymentValue: number;
        equityDownAmount: number;
        ltv: {
            maximum: number;
            achieved: number;
            diff: number;
        };
        operatingCosts: {
            total: number;
        };
        paymentValue: {
            paymentValue: number;
            dstiRatio: {
                expected: number;
                achieved: number;
                diff: number;
                overGrossIncome: number;
            };
        };
        bindingConstraint: string;
    };
}

/** Interface for the result data returned by the mac-es endpoint */
export interface MacEsResult {
    prudente: MacEsScenarioData;
    ampliado: MacEsScenarioData;
}

/** Strings with i18n */
const LABELS = {
    prudenteScenario: __("Cenário prudente", "df-simulators"),
    ampliadoScenario: __("Cenário ampliado", "df-simulators"),
    valueToConsider: __("Valor a considerar num", "df-simulators"),
    effortTitle: __("Esforço", "df-simulators"),
    remaining: __("Restante", "df-simulators"),
    scenarioTitle: __(
        "Com estes rendimentos e capitais próprios, pode comprar um imóvel até <span>${property_value}</span>. A sua prestação será <span>${monthly_payment}</span>",
        "df-simulators"
    ),
    effortRateGood: __("Taxa de esforço saudável de ${percent}%", "df-simulators"),
    effortRateGoodDesc: __("A relação entre os rendimentos e encargos mensais estará equilibrada. ", "df-simulators"),
    effortRateBad: __("Taxa de esforço preocupante de ${percent}%", "df-simulators"),
    effortRateBadDesc: __("A relação entre os rendimentos e as despesas pode ser comprometida com facilidade.", "df-simulators"),
    compositionTab: __("Composição do cenário", "df-simulators"),
    comparisonTab: __("Comparação entre cenários", "df-simulators"),
    notesHeading: __("Notas", "df-simulators"),
    notesTitle: __("Aviso Legal", "df-simulators"),
    notesInfo: {
        interestRate: __("TAN: ${percent}", "df-simulators"),
        totalYears: __("Prazo: ${years} anos", "df-simulators"),
        ltv: __("LTV: ${percent}", "df-simulators"),
        spread: __("Spread indicativo: ${percent}", "df-simulators"),
        euribor: __("Euribor a 6 Meses: ${percent}", "df-simulators"),
        list: __(
            `<ul><li>O prémio de permanência cresce ao longo do tempo.</li>
<li>Os juros começam a contar após 3 meses.</li>
<li>Os juros são pagos e compostos trimestralmente.</li>
A Euribor usada pode ser configurável.</li></ul>`,
            "df-simulators"
        ),
        legal: __("A simulação apresentada é meramente indicativa e não constitui uma oferta vinculativa. A aprovação do crédito depende da análise de risco do banco.", "df-simulators"),
    },
};

/** Props for the ScenarioSelector component */
interface ScenarioSelectorProps {
    setScenario: React.Dispatch<React.SetStateAction<MacEsScenario>>;
    scenario: MacEsScenario;
}

/** Props for the TopBox component */
interface TopBoxProps {
    title: string;
    highlight: string;
    description: string;
    color: string;
    chartColor: string;
}

const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({ setScenario, scenario }) => (
    <>
        {LABELS.valueToConsider}
        <select onChange={(e) => setScenario(e.target.value as MacEsScenario)} value={scenario}>
            <option value="prudente">{LABELS.prudenteScenario}</option>
            <option value="ampliado">{LABELS.ampliadoScenario}</option>
        </select>
    </>
);

const MacEs: React.FC = () => {
    const { resultData } = useResult();
    const { response, loading } = resultData;

    const [scenario, setScenario] = useState<MacEsScenario>("prudente");
    const [apiDefault, setApiResult] = useState<MacEsResult | null>(null);
    const [topBox, setTopBox] = useState<TopBoxProps>({
        title: "",
        highlight: "",
        description: "",
        color: COLORS.GREEN,
        chartColor: COLORS.CHART_GREEN,
    });

    useEffect(() => {
        if (!response?.data?.result) return;
        const { prudente, ampliado } = response.data.result;
        setApiResult({ prudente, ampliado });
        setScenario("prudente");
    }, [response]);

    useEffect(() => {
        if (!apiDefault || !apiDefault[scenario]) return;

        const current = apiDefault[scenario];

        const effortRateRounded = Math.round(current.results.paymentValue.dstiRatio.achieved * 100);
        let color = COLORS.GREEN, chartColor = COLORS.CHART_GREEN;
        if (effortRateRounded > EFFORT_RATE_CRITICAL) {
            chartColor = COLORS.CHART_RED;
            color = COLORS.RED;
        } else if (effortRateRounded > EFFORT_RATE_WARNING) {
            chartColor = COLORS.CHART_YELLOW;
            color = COLORS.YELLOW;
        }

        const title = LABELS.scenarioTitle
            .replace("${property_value}", utils.toEuro(current.results.propertyValue))
            .replace("${monthly_payment}", utils.toEuro(current.results.paymentValue.paymentValue));

        const highlight = effortRateRounded > EFFORT_RATE_WARNING
            ? LABELS.effortRateBad.replace("${percent}", effortRateRounded.toString())
            : LABELS.effortRateGood.replace("${percent}", effortRateRounded.toString());
        const description = effortRateRounded > EFFORT_RATE_WARNING ? LABELS.effortRateBadDesc : LABELS.effortRateGoodDesc;

        setTopBox({ title, highlight, description, color, chartColor });
    }, [scenario, apiDefault]);

    if (!apiDefault || !apiDefault[scenario]) return null;

    const current = apiDefault[scenario];
    const effortRateRounded = Math.round(current.results.paymentValue.dstiRatio.achieved * 100);

    return (
        <div className="mac-es">
            <Box
                grid
                loading={loading}
                className="mac-es__top-box"
                header={{
                    titleAlt: <ScenarioSelector setScenario={setScenario} scenario={scenario} />,
                    info: utils.toEuro(current.results.propertyValue),
                    color: COLORS.TOP_BOX,
                }}
            >
                <div className="flex flex-col md:grid grid-cols-10 gap-8 items-center">
                    <div className="col-span-2 relative p-8 md:p-0 w-full mac-es__top-box__left">
                        <ChartPie
                            data={[
                                {
                                    name: LABELS.effortTitle,
                                    value: effortRateRounded,
                                    backgroundColor: topBox.chartColor,
                                },
                                {
                                    name: LABELS.remaining,
                                    value: 100 - effortRateRounded,
                                    backgroundColor: COLORS.CHART_REMAINING,
                                },
                            ]}
                            options={{
                                hover: false,
                                tooltip: { enabled: false },
                            }}
                            title=" "
                            percentage
                            number={effortRateRounded + "%"}
                        />
                    </div>
                    <div className="col-span-8 w-full md:w-auto mac-es__top-box__right">
                        <div
                            className="border-b border-grey-20 mb-3 pb-3 text-navy-100 font-bold text-lg [&_span]:text-orange-100"
                            dangerouslySetInnerHTML={{ __html: topBox.title }}
                        />
                        <div className="mac-es__top-box__right__sub-text">
                            <div className={`font-bold mb-1 text-${topBox.color}`}>{topBox.highlight}</div>
                            <div className="text-navy-70">{topBox.description}</div>
                        </div>
                    </div>
                </div>
            </Box>

            <Tabs
                loading={loading}
                data={[
                    {
                        heading: LABELS.compositionTab,
                        content: <TabComposition scenarioData={current} />,
                    },
                    {
                        heading: LABELS.comparisonTab,
                        content: <TabComparison simulationData={apiDefault} />,
                    },
                ]}
            />

            <Box
                backgroundColor="navy-5"
                textColor="navy-70"
                vertical
                header={{
                    title: LABELS.notesHeading,
                    textColor: "navy-70",
                }}
            >
                <IconContent
                    title=""
                    text={LABELS.notesInfo.list}
                    color="navy-70"
                    textColor="navy-70"
                />
                <IconContent
                    title={LABELS.notesTitle}
                    text={LABELS.notesInfo.legal}
                    color="navy-70"
                    textColor="navy-70"
                />
            </Box>

            {/* Share Simulation button */}
            <ShareFullLink />

            {/* Call-to-Action - Standard project pattern */}
            <Cta />
        </div>
    );
};

export default MacEs;
